import React, { useState, useMemo } from "react";
import type { ExplainResponse, Pillar } from "@nexus/contracts";
import { PILLARS } from "@nexus/contracts";
import { PILLAR_METADATA } from "../types";
import {
  X,
  ArrowRight,
  ShieldAlert,
  BarChart3,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Info,
  Sparkles,
} from "lucide-react";

interface ExplainPanelProps {
  data: ExplainResponse | null;
  onClose: () => void;
  entityNameKo?: string;
  entityNameEn?: string;
}

export const ExplainPanel: React.FC<ExplainPanelProps> = ({
  data,
  onClose,
  entityNameKo,
  entityNameEn,
}) => {
  const [selectedPillar, setSelectedPillar] = useState<Pillar | "ALL">("ALL");
  const [expandedIndicatorId, setExpandedIndicatorId] = useState<string | null>(null);

  const maxContribution = useMemo(() => {
    if (!data?.indicators) return 8.33;
    return Math.max(...data.indicators.map((i) => i.contributionToTotal), 8.33);
  }, [data]);

  if (!data) return null;

  const filteredIndicators =
    selectedPillar === "ALL"
      ? data.indicators
      : data.indicators.filter((ind) => ind.pillar === selectedPillar);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "6px",
                background: "rgba(99, 102, 241, 0.2)",
                border: "1px solid var(--accent-indigo)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart3 size={19} color="var(--accent-indigo)" />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2 style={{ fontSize: "17px", fontWeight: 700, color: "#ffffff" }}>
                  {entityNameKo || data.entityId.toUpperCase()} ({entityNameEn || data.entityId.toUpperCase()})
                </h2>
                <span className="badge-period">{data.period}</span>
                {data.lowConfidence && (
                  <span className="badge-low-conf font-mono">
                    <ShieldAlert size={10} style={{ display: "inline", marginRight: "3px" }} />
                    저신뢰 결측 경고 (커버리지 {Math.round(data.coverage * 100)}%)
                  </span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
                기술패권 종합 지수 투명 산출 근거 (Explain Pipeline) · 가중치 세트:{" "}
                <strong style={{ color: "#38bdf8" }}>{data.weightSet.label}</strong>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "10px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
                종합 지수 점수
              </div>
              <div
                className="font-mono"
                style={{ fontSize: "22px", fontWeight: 800, color: "var(--accent-cyan)" }}
              >
                {data.totalScore.toFixed(1)}
              </div>
            </div>
            <button className="btn-icon" onClick={onClose} title="닫기">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Transparent Decomposition Flow Banner */}
          <div className="explain-flow-banner">
            <div className="flow-step">
              <span className="flow-step-name">1. 원시 관측값</span>
              <span className="flow-step-val">RAW DATA</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">2. 스케일 변환</span>
              <span className="flow-step-val">TRANSFORM</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">3. 0~100 정규화</span>
              <span className="flow-step-val">NORMALIZED</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">4. 4대 기둥 점수</span>
              <span className="flow-step-val">PILLAR</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">5. 정책 가중치</span>
              <span className="flow-step-val">WEIGHT</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">6. 기여도 합성</span>
              <span className="flow-step-val">CONTRIBUTION</span>
            </div>
            <span className="flow-arrow"><ArrowRight size={13} /></span>
            <div className="flow-step">
              <span className="flow-step-name">7. 최종 종합 지수</span>
              <span className="flow-step-val" style={{ color: "#38bdf8" }}>TOTAL SCORE</span>
            </div>
          </div>

          {/* Pillars Breakdown Grid */}
          <div style={{ marginBottom: "20px" }}>
            <div className="section-title">
              <span style={{ color: "#e2e8f0" }}>4대 기둥별 점수 및 기여도 (PILLARS)</span>
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  className={`btn-secondary ${selectedPillar === "ALL" ? "active" : ""}`}
                  onClick={() => setSelectedPillar("ALL")}
                  style={{
                    backgroundColor: selectedPillar === "ALL" ? "rgba(99,102,241,0.3)" : undefined,
                    borderColor: selectedPillar === "ALL" ? "var(--accent-indigo)" : undefined,
                    color: selectedPillar === "ALL" ? "#ffffff" : undefined,
                  }}
                >
                  전체 지표 ({data.indicators.length})
                </button>
                {PILLARS.map((p) => {
                  const meta = PILLAR_METADATA[p];
                  const isSelected = selectedPillar === p;
                  return (
                    <button
                      key={p}
                      className="btn-secondary"
                      onClick={() => setSelectedPillar(p)}
                      style={{
                        borderColor: isSelected ? meta.color : undefined,
                        backgroundColor: isSelected ? `${meta.color}25` : undefined,
                        color: isSelected ? "#ffffff" : undefined,
                        fontWeight: isSelected ? 700 : 500,
                      }}
                    >
                      {meta.nameKo}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px" }}>
              {data.pillars.map((pillarExp) => {
                const meta = PILLAR_METADATA[pillarExp.pillar];
                const score = pillarExp.score;

                return (
                  <div
                    key={pillarExp.pillar}
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: "6px",
                      padding: "12px",
                      borderTop: `3px solid ${meta.color}`,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: meta.color, fontSize: "13px" }}>
                        {meta.nameKo} ({pillarExp.pillar})
                      </span>
                      <span className="font-mono" style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 600 }}>
                        가중치 {Math.round(pillarExp.weight * 100)}%
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "baseline", gap: "6px", margin: "8px 0 4px" }}>
                      <span
                        className="font-mono"
                        style={{ fontSize: "20px", fontWeight: 800, color: score !== null ? "#ffffff" : "var(--text-disabled)" }}
                      >
                        {score !== null ? score.toFixed(1) : "결측"}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>점</span>
                    </div>

                    <div style={{ fontSize: "12px", color: "var(--text-secondary)", display: "flex", justifyContent: "space-between" }}>
                      <span>종합 기여:</span>
                      <strong className="font-mono" style={{ color: "var(--accent-cyan)" }}>
                        +{pillarExp.contributionToTotal.toFixed(2)}pt
                      </strong>
                    </div>

                    <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>
                      관측 {pillarExp.observedCount}/{pillarExp.applicableCount}개 지표
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Indicators Detailed Table with High Contribution Highlighting (Priority 4) */}
          <div>
            <div className="section-title">
              <span style={{ color: "#e2e8f0" }}>세부 지표 산출 내역 ({filteredIndicators.length}개 지표)</span>
              <span style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                클릭하여 원시 출처 및 비교군 통계 펼치기
              </span>
            </div>

            <table className="indicator-table">
              <thead>
                <tr>
                  <th>지표 ID / 기둥</th>
                  <th style={{ textAlign: "right" }}>원시 관측값 (RAW)</th>
                  <th style={{ textAlign: "center" }}>변환 방식</th>
                  <th style={{ textAlign: "right" }}>정규화 점수</th>
                  <th style={{ textAlign: "right" }}>기둥 내 가중치</th>
                  <th style={{ textAlign: "right", width: "160px" }}>종합 기여도 (pt)</th>
                  <th style={{ width: "24px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredIndicators.map((ind) => {
                  const meta = PILLAR_METADATA[ind.pillar];
                  const isExpanded = expandedIndicatorId === ind.indicatorId;
                  const isDegenerate = ind.degenerate;
                  const isHighContribution = ind.contributionToTotal >= 6.5; // Top tier contributor
                  const contribPct = Math.min(100, Math.max(5, (ind.contributionToTotal / maxContribution) * 100));

                  return (
                    <React.Fragment key={ind.indicatorId}>
                      <tr
                        style={{
                          cursor: "pointer",
                          background: isExpanded
                            ? "rgba(99, 102, 241, 0.14)"
                            : isHighContribution
                            ? "rgba(56, 189, 248, 0.05)"
                            : undefined,
                        }}
                        onClick={() =>
                          setExpandedIndicatorId(isExpanded ? null : ind.indicatorId)
                        }
                      >
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <span
                              style={{
                                width: "7px",
                                height: "7px",
                                borderRadius: "50%",
                                backgroundColor: meta.color,
                              }}
                            />
                            <strong className="font-mono" style={{ color: isHighContribution ? "#38bdf8" : "#ffffff", fontSize: "12px" }}>
                              {ind.indicatorId}
                            </strong>
                            <span
                              style={{
                                fontSize: "10px",
                                color: meta.color,
                                background: `${meta.color}20`,
                                padding: "1px 5px",
                                borderRadius: "3px",
                                fontWeight: 600,
                              }}
                            >
                              {meta.nameKo}
                            </span>
                            {isHighContribution && (
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: "#38bdf8",
                                  background: "rgba(56, 189, 248, 0.15)",
                                  border: "1px solid rgba(56, 189, 248, 0.35)",
                                  padding: "1px 4px",
                                  borderRadius: "3px",
                                  fontWeight: 700,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "2px",
                                }}
                                title="종합 지수 산출에 크게 기여한 핵심 우위 지표"
                              >
                                <Sparkles size={8} /> 핵심 기여
                              </span>
                            )}
                            {isDegenerate && (
                              <span
                                style={{
                                  fontSize: "9px",
                                  color: "var(--accent-amber)",
                                  background: "rgba(245, 158, 11, 0.15)",
                                  padding: "1px 4px",
                                  borderRadius: "3px",
                                }}
                                title="비교 대상 전체가 동일한 값으로 관측되어 중립 점수(50.0)가 부여되었습니다."
                              >
                                축퇴 (동일값)
                              </span>
                            )}
                          </div>
                        </td>

                        <td style={{ textAlign: "right" }}>
                          <span className="font-mono" style={{ fontWeight: 700, color: "#f8fafc", fontSize: "12px" }}>
                            {ind.rawValue.toLocaleString()}
                          </span>
                          <span style={{ fontSize: "11px", color: "var(--text-muted)", marginLeft: "4px" }}>
                            {ind.unit}
                          </span>
                        </td>

                        <td style={{ textAlign: "center" }}>
                          <span className="font-mono" style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                            {ind.transform === "log10" ? "log₁₀" : "linear"}
                            <span style={{ color: "var(--text-muted)", marginLeft: "4px" }}>
                              ({ind.direction === "higher_better" ? "↑우위" : "↓우위"})
                            </span>
                          </span>
                        </td>

                        <td style={{ textAlign: "right" }}>
                          <span
                            className="font-mono"
                            style={{
                              fontWeight: 700,
                              fontSize: "13px",
                              color: ind.normalized >= 70 ? "var(--accent-cyan)" : ind.normalized >= 40 ? "#ffffff" : "var(--accent-rose)",
                            }}
                          >
                            {ind.normalized.toFixed(1)}
                          </span>
                          <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>/100</span>
                        </td>

                        <td style={{ textAlign: "right" }}>
                          <span className="font-mono" style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
                            {Math.round(ind.weightWithinPillar * 100)}%
                          </span>
                        </td>

                        {/* Enhanced Contribution Column with Visual Progress Bar (Priority 4) */}
                        <td style={{ textAlign: "right" }}>
                          <div className="contrib-bar-wrap">
                            <div className="contrib-bar-bg" title={`기여도 비중: ${contribPct.toFixed(0)}%`}>
                              <div className="contrib-bar-fill" style={{ width: `${contribPct}%` }} />
                            </div>
                            <span
                              className="font-mono"
                              style={{
                                fontWeight: 700,
                                fontSize: "12px",
                                color: isHighContribution ? "#38bdf8" : "#f1f5f9",
                              }}
                            >
                              +{ind.contributionToTotal.toFixed(2)}pt
                            </span>
                          </div>
                        </td>

                        <td style={{ textAlign: "center", color: "var(--text-muted)" }}>
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={7} style={{ background: "rgba(10, 16, 30, 0.95)", padding: "14px 18px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                              {/* Left: Source Citation */}
                              <div style={{ background: "rgba(19, 29, 51, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                                  <Info size={12} color="var(--accent-cyan)" /> 출처 및 인용 검증 (SOURCE & CITATION)
                                </div>
                                <div style={{ fontSize: "13px", fontWeight: 700, color: "#ffffff" }}>
                                  {ind.source.publisher}
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
                                  {ind.source.title}
                                </div>

                                <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                                  {ind.source.type === "placeholder" ? (
                                    <span style={{ fontSize: "10px", color: "var(--accent-rose)", background: "rgba(244, 63, 94, 0.18)", padding: "2px 6px", borderRadius: "3px", fontWeight: 600 }}>
                                      Placeholder source — synthetic V0
                                    </span>
                                  ) : ind.source.url ? (
                                    <a
                                      href={ind.source.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: "var(--accent-cyan)", fontSize: "11px", display: "flex", alignItems: "center", gap: "3px" }}
                                    >
                                      원문 문서 열기 <ExternalLink size={11} />
                                    </a>
                                  ) : null}

                                  <span className="font-mono" style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                    신뢰등급: {ind.source.reliability}
                                  </span>

                                  {ind.isEstimate && (
                                    <span style={{ fontSize: "10px", color: "var(--accent-amber)", background: "rgba(245, 158, 11, 0.18)", padding: "2px 5px", borderRadius: "3px", fontWeight: 600 }}>
                                      추정치 (Assessment: {ind.assessmentId || "N/A"})
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Right: Normalization Comparison Set */}
                              <div style={{ background: "rgba(19, 29, 51, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "4px" }}>
                                  정규화 비교군 통계 (COMPARISON SET)
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "12px" }}>
                                  <div>
                                    <span style={{ color: "var(--text-muted)" }}>최소값 (Min):</span>{" "}
                                    <strong className="font-mono" style={{ color: "#ffffff" }}>
                                      {ind.comparison.min.toLocaleString()} {ind.unit}
                                    </strong>
                                    {ind.comparison.minEntityId && (
                                      <span style={{ color: "var(--accent-cyan)", marginLeft: "4px", fontWeight: 600 }}>
                                        ({ind.comparison.minEntityId.toUpperCase()})
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <span style={{ color: "var(--text-muted)" }}>최대값 (Max):</span>{" "}
                                    <strong className="font-mono" style={{ color: "#ffffff" }}>
                                      {ind.comparison.max.toLocaleString()} {ind.unit}
                                    </strong>
                                    {ind.comparison.maxEntityId && (
                                      <span style={{ color: "var(--accent-cyan)", marginLeft: "4px", fontWeight: 600 }}>
                                        ({ind.comparison.maxEntityId.toUpperCase()})
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "6px" }}>
                                  비교군 크기: {ind.comparison.entityCount}개 국가 · 변환값:{" "}
                                  <span className="font-mono" style={{ color: "var(--accent-cyan)", fontWeight: 600 }}>
                                    {ind.transformedValue.toFixed(4)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
